import { useCallback, useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import {
  acknowledgeCheckinAction, executeReviewedCheckinAction, readPendingCheckinAction,
  type CheckinRequest, type CheckinResponse,
} from "@/lib/checkinExecutor";
import type { PendingConnectorWrite } from "@/lib/connectorPendingWrite";

/** A modal keeps the original review until its durable result and ACK are known. */
export function useCheckInActionConfirm(ownerId: string) {
  const [pending, setPending] = useState<PendingConnectorWrite | null>(null);
  const [ready, setReady] = useState(false), [busy, setBusy] = useState(false), [error, setError] = useState<string | null>(null);
  const epoch = useRef(0), mounted = useRef(false), lock = useRef(false), readyRef = useRef(false);
  const pendingRef = useRef<PendingConnectorWrite | null>(null);
  const replace = useCallback((entry: PendingConnectorWrite | null) => { pendingRef.current = entry; setPending(entry); }, []);
  useEffect(() => {
    mounted.current = true; const run = ++epoch.current;
    readyRef.current = false; lock.current = false; setReady(false); setBusy(false); replace(null); setError(null);
    const current = () => mounted.current && run === epoch.current;
    const assert = () => { if (!current()) throw new Error("invalid_session"); };
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session?.user.id === ownerId) return;
      epoch.current++; readyRef.current = false; lock.current = true;
      setReady(false); setBusy(false); replace(null); setError("invalid_session");
    });
    if (ownerId) void (async () => {
      try {
        const saved = await readPendingCheckinAction(ownerId, assert); assert();
        replace(saved); readyRef.current = true; setReady(true);
      } catch (cause) { if (current()) setError(cause instanceof Error ? cause.message : "pending_storage_unavailable"); }
    })();
    const lifecycle = epoch;
    return () => { mounted.current = false; lifecycle.current++; readyRef.current = false; subscription.unsubscribe(); };
  }, [ownerId, replace]);
  const captureCurrent = () => { const run = epoch.current; return () => mounted.current && readyRef.current && run === epoch.current; };
  const execute = async (input?: CheckinRequest): Promise<{ entry: PendingConnectorWrite; response: CheckinResponse } | null> => {
    if (!readyRef.current || lock.current) return null;
    const current = captureCurrent(), assert = () => { if (!current()) throw new Error("invalid_session"); };
    // Clone synchronously, before auth or journal lookup. Never substitute a new review after await.
    const original = input ?? pendingRef.current?.body;
    if (!original) { setError("review_required"); return null; }
    let request: CheckinRequest;
    try { request = structuredClone(original) as CheckinRequest; } catch { setError("invalid_request"); return null; }
    lock.current = true; setBusy(true); setError(null);
    try {
      const done = await executeReviewedCheckinAction(ownerId, request, assert); assert(); replace(done.entry); return done;
    } catch (cause) {
      if (current()) {
        setError(cause instanceof Error ? cause.message : "outcome_unknown");
        try { const saved = await readPendingCheckinAction(ownerId, assert); assert(); replace(saved); } catch { if (current()) setError("pending_storage_unavailable"); }
      }
      return null;
    } finally { if (current()) { lock.current = false; setBusy(false); } }
  };
  const acknowledge = async () => {
    const entry = pendingRef.current;
    if (!entry || entry.status !== "succeeded" || !readyRef.current || lock.current) return false;
    const current = captureCurrent(), assert = () => { if (!current()) throw new Error("invalid_session"); };
    lock.current = true; setBusy(true); setError(null);
    try { await acknowledgeCheckinAction(entry, assert); assert(); replace(null); return true; }
    catch (cause) { if (current()) setError(cause instanceof Error ? cause.message : "pending_storage_unavailable"); return false; }
    finally { if (current()) { lock.current = false; setBusy(false); } }
  };
  return { pending, ready, busy, error, execute, acknowledge, captureCurrent };
}
